//
// ============================================================================
// IMPORTS
// ============================================================================
//

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * useOtpVisit composable
 *
 * Resolves and caches the visit ID for the currently authenticated OTP patient.
 * The visit ID is required to call GET /visits/{visitId}/results and
 * GET /visits/{visitId}/balance, and to build correct navigation drawer links.
 *
 * Responsibilities:
 * - Extract the visit ID from the Keycloak preferred_username.
 *   OTP patient usernames are formatted as "otp-{visitId}" (e.g. "otp-737655"),
 *   so the last segment after splitting on "-" IS the visit ID directly.
 *   No API discovery call is needed or correct — calling GET /patients/{id}/visits
 *   would be wrong because the extracted ID is a visit ID, not a patient ID.
 * - Cache the resolved ID in shared state so drawer links and the results page
 *   do not need to re-compute
 * - Allow the results page to set visitId directly (e.g. from route params) so
 *   drawer active-path rules work when landing directly on the results URL
 * - Provide a refresh trigger counter that the results page watches to force
 *   a silent re-fetch without a loading overlay
 * - patientId (from useAuth) may be null on first mount if getUserInfo() is
 *   still in-flight. resolveVisitId returns null when not yet ready; the caller
 *   (index.vue) watches patientId and retries once it becomes non-null.
 */
export const useOtpVisit = () => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { patientId } = useAuth();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  const visitId = useState<string | null>("otp.visitId", () => null);
  const loading = useState<boolean>("otp.visitLoading", () => false);
  const error = useState<string | null>("otp.visitError", () => null);

  /**
   * Incrementing counter that the results page watches.
   * Each increment triggers a silent background refetch of results data.
   */
  const refreshTrigger = useState<number>("otp.refreshTrigger", () => 0);

  /**
   * Incrementing counter that the balance page watches.
   * Each increment triggers a silent background refetch of balance data.
   */
  const balanceRefreshTrigger = useState<number>("otp.balanceRefreshTrigger", () => 0);

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Resolve the OTP patient's visit ID.
   *
   * Purpose:
   * The Keycloak preferred_username for OTP patients is "otp-{visitId}"
   * (e.g. "otp-737655"). The last segment after splitting on "-" is the
   * visit ID directly. No API call is needed.
   *
   * Returns null — without setting an error — when patientId is not yet
   * available (getUserInfo() still in-flight), so the caller can retry
   * via a watcher.
   *
   * @param _signal - Unused; kept for interface compatibility
   * @returns The visit ID string, or null if auth not yet ready
   */

  const resolveVisitId = (_signal?: AbortSignal | null): string | null => {
    if (visitId.value) return visitId.value;
    if (!patientId.value) return null;
    visitId.value = patientId.value;
    return visitId.value;
  };

  /**
   * Set the visit ID directly from a known value (e.g. route param).
   *
   * Purpose:
   * When the user lands directly on the results URL the index page is never
   * mounted so resolveVisitId is never called. The results page calls setVisitId
   * with the route param so drawer active-path rules are populated immediately.
   *
   * @param id - Visit ID string (e.g. route.params.id)
   */
  const setVisitId = (id: string) => {
    if (!visitId.value) {
      visitId.value = id;
    }
  };

  /**
   * Increment the refresh trigger counter.
   *
   * Purpose:
   * The results page watches this counter and performs a silent background
   * refetch each time it increments. Uses a leading-edge 500ms debounce
   * (same pattern as useValidationRefresh) to prevent rapid duplicate fetches
   * from double-clicks or accidental button spam.
   */
  let lastRefreshTime = 0;

  const triggerRefresh = () => {
    const now = Date.now();
    if (now - lastRefreshTime < 500) return;
    lastRefreshTime = now;
    refreshTrigger.value += 1;
  };

  /**
   * Increment the balance refresh trigger counter.
   *
   * Purpose:
   * The balance page watches this counter and performs a cache-clearing
   * refetch each time it increments. Uses the same leading-edge 500ms
   * debounce pattern as triggerRefresh.
   */
  let lastBalanceRefreshTime = 0;

  const triggerBalanceRefresh = () => {
    const now = Date.now();
    if (now - lastBalanceRefreshTime < 500) return;
    lastBalanceRefreshTime = now;
    balanceRefreshTrigger.value += 1;
  };

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    visitId: readonly(visitId),
    loading: readonly(loading),
    error: readonly(error),
    refreshTrigger: readonly(refreshTrigger),
    balanceRefreshTrigger: readonly(balanceRefreshTrigger),
    resolveVisitId,
    setVisitId,
    triggerRefresh,
    triggerBalanceRefresh,
  } as const;
};
